import { useEffect, useMemo, useRef, useState } from 'react'
import { ArrowRight, Search, X } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'

import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from '../ui/dialog'
import { useAuth } from '../../hooks/useAuth'
import type { DashboardUserRole } from '../../lib/api/auth'
import { cn } from '../../utils/cn'
import { getDashboardRoutesForRole, type DashboardRouteSearchItem } from './nav-items'

type RankedRoute = {
  route: DashboardRouteSearchItem
  score: number
  index: number
}

const normalizeSearchText = (value: string) => value.trim().toLowerCase()

const getRouteSearchBlob = (route: DashboardRouteSearchItem) => {
  return [
    route.label,
    route.groupLabel,
    route.path.replaceAll('/', ' '),
    ...(route.keywords ?? [])
  ].join(' ').toLowerCase()
}

const scoreRoute = (route: DashboardRouteSearchItem, query: string) => {
  if (!query) {
    return 0
  }

  const label = route.label.toLowerCase()
  const group = route.groupLabel.toLowerCase()
  const path = route.path.toLowerCase()
  const keywords = route.keywords?.map((keyword) => keyword.toLowerCase()) ?? []
  const blob = getRouteSearchBlob(route)

  if (label === query) {
    return 1
  }

  if (label.startsWith(query)) {
    return 2
  }

  if (keywords.some((keyword) => keyword === query || keyword.startsWith(query))) {
    return 3
  }

  if (group.startsWith(query)) {
    return 4
  }

  if (label.includes(query)) {
    return 5
  }

  if (keywords.some((keyword) => keyword.includes(query))) {
    return 6
  }

  if (group.includes(query)) {
    return 7
  }

  if (blob.includes(query) || path.includes(query)) {
    return 8
  }

  return null
}

const getSearchResults = (routes: DashboardRouteSearchItem[], query: string) => {
  const normalizedQuery = normalizeSearchText(query)

  return routes
    .map<RankedRoute | null>((route, index) => {
      const score = scoreRoute(route, normalizedQuery)
      return score === null ? null : { route, score, index }
    })
    .filter((item): item is RankedRoute => item !== null)
    .sort((a, b) => a.score - b.score || a.index - b.index)
    .map((item) => item.route)
}

export const DashboardCommandPalette = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)

  const role = typeof user?.role === 'string' ? user.role as DashboardUserRole : undefined
  const routes = useMemo(() => getDashboardRoutesForRole(role), [role])
  const results = useMemo(() => getSearchResults(routes, query), [routes, query])

  useEffect(() => {
    const handleGlobalShortcut = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.altKey || event.shiftKey) {
        return
      }

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setIsOpen(true)
      }
    }

    window.addEventListener('keydown', handleGlobalShortcut)
    return () => window.removeEventListener('keydown', handleGlobalShortcut)
  }, [])

  const openPalette = () => {
    setIsOpen(true)
  }

  const closePalette = () => {
    setIsOpen(false)
    setQuery('')
  }

  const goToRoute = (route: DashboardRouteSearchItem) => {
    closePalette()
    if (location.pathname !== route.path) {
      navigate(route.path)
    }
  }

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setSelectedIndex((current) => (results.length === 0 ? 0 : (current + 1) % results.length))
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault()
      setSelectedIndex((current) => (results.length === 0 ? 0 : (current - 1 + results.length) % results.length))
    }

    const activeIndex = results.length === 0 ? 0 : Math.min(selectedIndex, results.length - 1)

    if (event.key === 'Enter' && results[activeIndex]) {
      event.preventDefault()
      goToRoute(results[activeIndex])
    }
  }

  const activeIndex = results.length === 0 ? 0 : Math.min(selectedIndex, results.length - 1)
  const activeResultId = results[activeIndex] ? `command-palette-result-${activeIndex}` : undefined

  return (
    <>
      <button
        type="button"
        onClick={openPalette}
        aria-label="Open command palette"
        aria-keyshortcuts="Meta+K Control+K"
        className={cn(
          'flex h-9 min-w-0 items-center gap-2 rounded-lg border border-border bg-card px-2.5 text-[13px] font-normal text-muted-foreground',
          'hover:border-primary/30 hover:bg-primary/5 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40'
        )}
      >
        <Search className="h-4 w-4 shrink-0" />
        <span className="hidden max-w-52 truncate sm:inline">Search Base Camp</span>
        <kbd aria-hidden="true" className="ml-4 hidden rounded border border-border px-1.5 py-0.5 font-sans text-[10px] text-muted-foreground xl:inline">⌘ / Ctrl K</kbd>
      </button>

      <Dialog open={isOpen} onOpenChange={(open) => (open ? setIsOpen(true) : closePalette())}>
        <DialogContent
          className="top-[8vh] w-[min(94vw,720px)] overflow-hidden p-0"
          onOpenAutoFocus={(event) => {
            event.preventDefault()
            inputRef.current?.focus()
          }}
        >
          <div className="flex items-center gap-3 border-b border-border px-4 py-3">
            <Search className="h-5 w-5 shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1">
              <DialogTitle className="sr-only">Search Base Camp</DialogTitle>
              <DialogDescription className="sr-only">
                Search available Base Camp pages and navigate to one result.
              </DialogDescription>
              <input
                ref={inputRef}
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value)
                  setSelectedIndex(0)
                }}
                onKeyDown={handleInputKeyDown}
                role="combobox"
                aria-label="Search Base Camp"
                aria-controls="command-palette-results"
                aria-expanded={isOpen}
                aria-activedescendant={activeResultId}
                autoComplete="off"
                placeholder="Search pages and tools"
                className="h-10 w-full bg-transparent text-base font-semibold text-foreground placeholder:text-muted-foreground focus:outline-none"
              />
            </div>
            <DialogClose
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              aria-label="Close command palette"
            >
              <X className="h-4 w-4" />
            </DialogClose>
          </div>

          <div id="command-palette-results" role="listbox" className="max-h-[58vh] overflow-y-auto p-2">
            {results.length > 0 ? (
              results.map((route, index) => {
                const Icon = route.icon
                const isSelected = index === activeIndex
                return (
                  <button
                    key={route.path}
                    id={`command-palette-result-${index}`}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onMouseEnter={() => setSelectedIndex(index)}
                    onClick={() => goToRoute(route)}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left transition-colors',
                      isSelected ? 'bg-primary text-primary-foreground shadow-sm' : 'text-foreground hover:bg-muted'
                    )}
                  >
                    <span className={cn(
                      'flex h-9 w-9 shrink-0 items-center justify-center rounded-md',
                      isSelected ? 'bg-primary-foreground/20' : 'bg-muted text-muted-foreground'
                    )}>
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold">{route.label}</span>
                      <span className={cn(
                        'block truncate text-xs',
                        isSelected ? 'text-primary-foreground/80' : 'text-muted-foreground'
                      )}>
                        {route.groupLabel} / {route.path}
                      </span>
                    </span>
                    <ArrowRight className={cn(
                      'h-4 w-4 shrink-0',
                      isSelected ? 'text-primary-foreground/80' : 'text-muted-foreground'
                    )} />
                  </button>
                )
              })
            ) : (
              <div className="px-4 py-10 text-center">
                <p className="text-sm font-semibold text-foreground">No matching destinations</p>
                <p className="mt-1 text-xs text-muted-foreground">No results for this search.</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

export { getSearchResults }
